import { useState, useEffect, useCallback } from 'react';
import { fetchOpenPositions } from '../api/apiService';

/**
 * @typedef {Object} Position
 * @property {string} id
 * @property {string} account_id
 * @property {string} symbol_id
 * @property {string} symbol_name
 * @property {'buy'|'sell'} side
 * @property {number} qty
 * @property {string} entry_price
 * @property {string|null} current_price
 * @property {number} unrealized_pl
 * @property {number} market_value
 * @property {number} margin_used
 * @property {string|null} stop_loss
 * @property {string|null} take_profit
 * @property {'open'} status
 */

// The positions endpoint takes a UUID accountId and returns HTTP 400 for
// anything else. Validate locally so we never fire a request that is
// guaranteed to fail.
const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const isValidUuid = (value) =>
  typeof value === 'string' && UUID_REGEX.test(value);

// The NestJS trading relay emits positions in camelCase (positionId,
// entryPrice, currentPrice, unrealizedPl, symbol, ...), whereas the portal
// REST API and the dashboard table use snake_case (id, entry_price,
// current_price, unrealized_pl, symbol_name). Normalize each incoming row to
// the snake_case shape, keyed by the canonical `id` (= positionId), and only
// include fields that are actually present so that merging an update never
// wipes values (e.g. stop_loss / take_profit) that only REST provides.
const normalizeWSPosition = (row, accountId) => {
  if (!row || typeof row !== 'object') return null;
  const id = row.id ?? row.positionId ?? row.position_id;
  if (id == null) return null;

  const out = { id };

  const accId = row.account_id ?? row.accountId ?? accountId;
  if (accId != null) out.account_id = accId;

  const symbolId = row.symbol_id ?? row.symbolId;
  if (symbolId != null) out.symbol_id = symbolId;

  const symbolName = row.symbol_name ?? row.symbol;
  if (symbolName != null) out.symbol_name = symbolName;

  const entryPrice = row.entry_price ?? row.entryPrice;
  if (entryPrice != null) out.entry_price = entryPrice;

  const currentPrice = row.current_price ?? row.currentPrice;
  if (currentPrice != null) out.current_price = currentPrice;

  const unrealizedPl = row.unrealized_pl ?? row.unrealizedPl ?? row.totalPnl;
  if (unrealizedPl != null) out.unrealized_pl = unrealizedPl;

  const marketValue = row.market_value ?? row.marketValue;
  if (marketValue != null) out.market_value = marketValue;

  const stopLoss = row.stop_loss ?? row.stopLoss;
  if (stopLoss != null) out.stop_loss = stopLoss;

  const takeProfit = row.take_profit ?? row.takeProfit;
  if (takeProfit != null) out.take_profit = takeProfit;

  const marginUsed = row.margin_used ?? row.marginUsed;
  if (marginUsed != null) out.margin_used = marginUsed;

  if (row.side != null) out.side = row.side;
  if (row.qty != null) out.qty = row.qty;
  if (row.status != null) out.status = row.status;

  return out;
};

/**
 * Fetches open positions for every provided trading account and aggregates
 * them into a single list plus a total count.
 *
 * The endpoint is per-account, so one request is issued per valid UUID id and
 * the results are combined. Requests that fail (an account with no portal
 * data, a transient error, etc.) are skipped so a single bad account never
 * blanks out the whole dashboard.
 *
 * @param {string|undefined|null} accessToken Portal access token.
 * @param {Array<string>} [accountIds=[]] Trading account ids to query.
 * @returns {{ positions: Position[], totalCount: number, loading: boolean, error: string|null }}
 */
const useOpenPositions = (accessToken, accountIds = []) => {
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Stable key so we only refetch when the set of account ids actually changes
  // (financialData can be replaced by WebSocket updates with identical ids).
  const key = JSON.stringify([...accountIds].sort());

  useEffect(() => {
    const ids = JSON.parse(key).filter(isValidUuid);

    if (!accessToken || ids.length === 0) {
      setPositions([]);
      setError(null);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    Promise.all(
      ids.map((id) =>
        fetchOpenPositions(accessToken, id)
          .then((res) => (res.error ? [] : res.data?.positions ?? []))
          .catch(() => [])
      )
    )
      .then((results) => {
        if (cancelled) return;
        setPositions(results.flat());
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setPositions([]);
        setError('Unable to load positions.');
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [accessToken, key]);

  // Reconcile a real-time position frame from the WebSocket relay into the
  // aggregated `positions` state. Two shapes are supported:
  //
  //   POSITION_SNAPSHOT — full replacement for a single account:
  //     { type: 'POSITION_SNAPSHOT', accountId, positions: [...] }
  //
  //   POSITION_UPDATE — a delta for a single account:
  //     { type: 'POSITION_UPDATE',
  //       event: { type: 'POSITION_CREATED'|'POSITION_UPDATED'|'POSITION_CLOSED',
  //                accountId, data: [...] } }
  //
  // Positions are matched by `id`; an account is matched by `account_id` (the
  // REST field name). Incoming rows are stamped with the frame's accountId when
  // they do not already carry an `account_id`, so snapshot scoping stays correct.
  // Rows without an `id` are ignored for deltas since they cannot be matched.
  const applyPositionMessage = useCallback((message) => {
    if (!message || typeof message !== 'object') return;

    // Pull an id out of either the snake_case (REST) or camelCase (WS) shape.
    const idOf = (item) =>
      typeof item === 'string'
        ? item
        : item && (item.id ?? item.positionId ?? item.position_id);

    if (message.type === 'POSITION_SNAPSHOT') {
      const accountId = message.accountId;
      const incoming = Array.isArray(message.positions) ? message.positions : [];
      const normalized = incoming
        .map((p) => normalizeWSPosition(p, accountId))
        .filter(Boolean);

      setPositions((prev) => {
        // Replace only this account's slice; keep every other account intact.
        const rest = accountId
          ? prev.filter((p) => p.account_id !== accountId)
          : [];
        return [...rest, ...normalized];
      });
      return;
    }

    if (message.type === 'POSITION_UPDATE' && message.event) {
      const { type: eventType, accountId, data } = message.event;
      const raw = Array.isArray(data) ? data : data ? [data] : [];
      const upper = (eventType || '').toUpperCase();
      const isClose =
        upper.includes('CLOSED') ||
        upper.includes('DELETED') ||
        upper.includes('REMOVED');

      setPositions((prev) => {
        if (isClose) {
          const removedIds = new Set(raw.map(idOf).filter((id) => id != null));
          return prev.filter((p) => !removedIds.has(p.id));
        }

        const incoming = raw
          .map((item) => normalizeWSPosition(item, accountId))
          .filter(Boolean);

        const incomingById = new Map(incoming.map((p) => [p.id, p]));
        const updated = prev.map((p) =>
          incomingById.has(p.id) ? { ...p, ...incomingById.get(p.id) } : p
        );
        const existingIds = new Set(prev.map((p) => p.id));
        const added = incoming.filter((p) => !existingIds.has(p.id));
        return [...updated, ...added];
      });
    }
  }, []);

  return {
    positions,
    totalCount: positions.length,
    loading,
    error,
    applyPositionMessage,
  };
};

export default useOpenPositions;
